import type {
  UseModelToggleFavoritePayload,
  UseModelToggleFavoriteProps,
} from "./model-favorite.type";
import type { ApiError } from "@/services/api";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import get from "lodash/get";
import { useTranslation } from "react-i18next";

import { client } from "@/config/client";
import { useAppNotification } from "@/modules/app-notification";
import { getModelApiKey, getModelQueryKey } from "@/modules/model";

export const useModelToggleFavorite = <
  Payload extends UseModelToggleFavoritePayload = UseModelToggleFavoritePayload,
>({
  modelKey,
  id,
  options,
}: UseModelToggleFavoriteProps) => {
  const { t } = useTranslation();
  const { notify } = useAppNotification();
  const queryClient = useQueryClient();

  return useMutation<Payload, ApiError>({
    ...options,
    mutationFn: () => {
      const modelApiKey = getModelApiKey(modelKey);
      return get(client, [modelApiKey, "favorite", "toggle"])(id);
    },
    onSuccess: () => {
      const modelQueryKey = getModelQueryKey(modelKey);

      // Return queries invalidation promise to force mutation to stay in `pending`
      // state (aka isLoading=true) until refetch is finished.
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [modelQueryKey, "get", id] }),
        queryClient.invalidateQueries({ queryKey: ["user/favorites"] }),
      ]);
    },
    onError: () => {
      notify.error({ title: t("global:favoriteError") });
    },
  });
};
